import type { UUID } from '@aline/common';
import { getRankForIndex } from '@aline/common';

/**
 * The order of rules under each category, keyed by category id, as the rules
 * list shows it mid-drag. Only ids: the rule data itself stays in the Apollo
 * cache, and this is just the part of it a drag rearranges.
 *
 * Category ids and rule ids share one namespace as dnd-kit droppable ids — a
 * category card is a drop target so a rule can land in an empty one — so the
 * helpers below tell them apart by whether the id is a key of this record.
 */
export type RuleOrder = Record<UUID, readonly UUID[]>;

interface RankedRule {
  id: UUID;
}

interface CategoryWithRules {
  id: UUID;
  rules: readonly RankedRule[];
}

/** The order the server sent, which is what a drag starts from. */
export function getRuleOrder(categories: readonly CategoryWithRules[]): RuleOrder {
  return Object.fromEntries(
    categories.map(category => [category.id, category.rules.map(rule => rule.id)])
  );
}

/**
 * The category a droppable id belongs to: the id itself if it names a category,
 * else the category whose list holds the rule. Null for an id in neither, which
 * happens when the list re-renders from fresh data mid-drag.
 */
export function findRuleCategoryId(order: RuleOrder, id: string): UUID | null {
  if (id in order) return id;

  return Object.keys(order).find(categoryId => order[categoryId].includes(id)) ?? null;
}

interface MoveRuleToCategoryOptions {
  /**
   * Whether the dragged rule sits below the rule it is over, in which case it
   * lands after that rule rather than before it. Ignored when `overId` is a
   * category.
   */
  isBelowOver: boolean;
  /** The droppable the rule is over: a rule in the target category, or the category itself. */
  overId: string;
  ruleId: UUID;
  toCategoryId: UUID;
}

/**
 * Lifts a rule out of whichever category holds it and files it under
 * `toCategoryId`, next to the rule it is over — or at the end when it is over
 * the category rather than a rule in it, which is how a rule lands in an empty
 * category. Returns the same order when the rule is already there.
 */
export function moveRuleToCategory(
  order: RuleOrder,
  options: MoveRuleToCategoryOptions
): RuleOrder {
  const { isBelowOver, overId, ruleId, toCategoryId } = options;
  const fromCategoryId = findRuleCategoryId(order, ruleId);
  if (!fromCategoryId || fromCategoryId === toCategoryId) return order;

  const targetRules = order[toCategoryId];
  if (!targetRules) return order;

  const overIndex = targetRules.indexOf(overId);
  const toIndex = overIndex === -1 ? targetRules.length : overIndex + (isBelowOver ? 1 : 0);

  return {
    ...order,
    [fromCategoryId]: order[fromCategoryId].filter(id => id !== ruleId),
    [toCategoryId]: [...targetRules.slice(0, toIndex), ruleId, ...targetRules.slice(toIndex)],
  };
}

/**
 * Moves a rule to the slot of another rule in the same category, shifting the
 * rules in between. Returns the same order when either rule is not in the
 * category, or when they are the same rule.
 */
export function moveRuleWithinCategory(
  order: RuleOrder,
  categoryId: UUID,
  ruleId: UUID,
  overRuleId: UUID
): RuleOrder {
  const rules = order[categoryId];
  if (!rules) return order;

  const fromIndex = rules.indexOf(ruleId);
  const toIndex = rules.indexOf(overRuleId);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return order;

  const without = rules.filter(id => id !== ruleId);

  return {
    ...order,
    [categoryId]: [...without.slice(0, toIndex), ruleId, ...without.slice(toIndex)],
  };
}

/**
 * Whether a drag may carry a rule into a category. A closed category (one
 * that is itself a pending suggestion, whose rejection deletes everything in
 * it) only takes back the rules that live in it. The server refuses the same
 * move.
 */
export function getCanEnterCategory(options: {
  closedCategoryIds: ReadonlySet<UUID>;
  /** The category the server has the rule in. */
  homeCategoryId: UUID | null;
  toCategoryId: UUID;
}): boolean {
  const { closedCategoryIds, homeCategoryId, toCategoryId } = options;
  return !closedCategoryIds.has(toCategoryId) || homeCategoryId === toCategoryId;
}

export interface RuleDrop {
  categoryId: UUID;
  /**
   * Where the rule sits among the category's other rules — what the move
   * mutation takes as `toIndex`. Equal to the rule's index in the final list,
   * because every rule before it there is one of those others.
   */
  toIndex: number;
}

/** Where a rule sits in `order`, or null if no category holds it. */
export function findRuleDrop(order: RuleOrder, ruleId: UUID): RuleDrop | null {
  const categoryId = findRuleCategoryId(order, ruleId);
  if (!categoryId || categoryId === ruleId) return null;

  return { categoryId, toIndex: order[categoryId].indexOf(ruleId) };
}

/**
 * The rank a dropped item is expected to get at `toIndex` among
 * `siblingRanks` (its new siblings, itself excluded, in order), for showing
 * the drop before the server replies. Null when two neighbours share a rank:
 * the rank generator refuses that, and a drop that cannot be shown early
 * must still be sent, so this never throws.
 */
export function getLandedRank(siblingRanks: readonly string[], toIndex: number): string | null {
  const isAscending = siblingRanks.every((rank, index) => {
    if (index === 0) return true;
    const previousRank = siblingRanks.at(index - 1);
    return previousRank !== undefined && previousRank < rank;
  });
  return isAscending ? getRankForIndex(siblingRanks, toIndex) : null;
}
